<template>
    <div class="grid md:grid-cols-12 grid-cols-1">
        <div class="col-search col-geo">
            <input
                id="location-search"
                autocomplete="off"
                class="input-search"
                placeholder="Where to / Property ID"
                type="text"
                v-model="keywords"
                v-on:click="resetSearch()"
            />
            <span class="icon-location"></span>
            <div class="predictions" v-if="results.length > 0">
                <div
                    class="predictions__row"
                    v-for="(result, index) in results"
                    v-html="highlight(result)"
                    v-on:click="selectResult(result, index)"
                ></div>
            </div>
        </div>
        <div class="col-search col-date">
            <div id="index-date" class="datepicker-trigger" ref="dateTrigger">
                <input
                    @click="lazyDP"
                    title="Enter dates for availability"
                    class="input-search cursor-pointer"
                    type="text"
                    id="trigger-range"
                    :value="formatDates(date1, date2)"
                    placeholder="Enter dates for availability"
                    readonly
                />
                <span class="icon-calendar" style="left: 16px"></span>
                <SearchCalendar
                    v-if="isDPVisible"
                    ref="calendarPopup"
                    :date-one="date1"
                    :date-two="date2"
                    :min-date="getToday"
                    :flexible-date="storeState.flexibleNights"
                    @close-calendar="closeCalendar"
                    @cancelled="onCancelMethod"
                    @range-selected="setFlexibleDate"
                    @load-search="loadSearch"
                    v-on:date-one-selected="
                        val => {
                            date1 = val;
                        }
                    "
                    v-on:date-two-selected="
                        val => {
                            date2 = val;
                        }
                    "
                    class="search-calendar-popup"
                    :class="{
                        'homepage-style': isHomePage,
                        'destination-style': !isHomePage
                    }"
                ></SearchCalendar>
            </div>
        </div>
        <div class="col-search col-button">
            <button
                @click="loadSearch()"
                :disabled="!searchUrl || isSearching"
                class="search-button relative flex items-center justify-center"
                type="button"
                id="button_find"
            >
                <!--                added a spinner so when mobile apply fires they know it's loading-->
                <span
                    v-if="isSearching"
                    class="absolute inset-0 flex items-center justify-center"
                >
                    <svg
                        aria-hidden="true"
                        class="w-5 h-5 animate-spin text-white fill-gray-600"
                        viewBox="0 0 100 101"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858
             100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50
             0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144
             50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987
             91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921
             72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921
             9.08144 50.5908Z"
                            fill="currentColor"
                        />
                        <path
                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116
             97.0079 33.5539C95.2932 28.8227 92.871 24.3692
             89.8167 20.348C85.8452 15.1192 80.8826 10.7238
             75.2124 7.41289C69.5422 4.10194 63.2754 1.94025
             56.7698 1.05124C51.7666 0.367541 46.6976 0.446843
             41.7345 1.27873C39.2613 1.69328 37.813 4.19778
             38.4501 6.62326C39.0873 9.04874 41.5694 10.4717
             44.0505 10.1071C47.8511 9.54855 51.7191 9.52689
             55.5402 10.0491C60.8642 10.7766 65.9928 12.5457
             70.6331 15.2552C75.2735 17.9648 79.3347 21.5619
             82.5849 25.841C84.9175 28.9121 86.7997 32.2913
             88.1811 35.8758C89.083 38.2158 91.5421 39.6781
             93.9676 39.0409Z"
                            fill="fill-blue-600"
                        />
                    </svg>
                </span>
                <span :class="{ 'opacity-0': isSearching }">Search</span>
            </button>
        </div>
    </div>
</template>

<script>
import {store, mutations} from "./store.js";
import debounce from "lodash/debounce";

import format from "date-fns/format";
import addDays from "date-fns/add_days";
import startOfToday from "date-fns/start_of_today";
import differenceInCalendarDays from "date-fns/difference_in_calendar_days";
import SearchCalendar from "./SearchCalendar.vue";

export default {
    name: "SearchFormComponent",
    components: {SearchCalendar},
    props: ["location", "parsedUrl"],
    data() {
        return {
            storeState: store.state,
            selected: "",
            clicked: false,
            keywords: null,
            url: "",
            placeId: "",
            type: "",
            results: [],
            dest: [],
            searchOff: true,
            aid: "",
            geolock: null,
            isDPVisible: false,
            showPicker: false,
            date1: "",
            date2: "",
            today: "",
            dateFormat: "D MMM YYYY",
            arriveFormat: "D-MMM-YYYY",
            isSearching: false
        };
    },
    watch: {
        keywords(after, before) {
            if (this.keywords.length < 3) {
                this.results = [];
                this.sendPID(0);
            }
            if (/([pP])([0-9]{3,})/.test(this.keywords) === true) {
                this.sendPID(this.keywords.substr(1));
            } else if (this.keywords.length > 3 && !isNaN(this.keywords)) {
                this.sendPID(this.keywords);
            } else if (this.keywords.length > 3 && this.clicked === false) {
                this.debouncedGetResults();
                this.sendPID(0);
            }
        },
        isDPVisible(newVal) {
            const bottomFade = document.getElementById("bottom_fade");
            if (bottomFade) {
                if (newVal) {
                    bottomFade.classList.remove("z-40");
                } else {
                    bottomFade.classList.add("z-40");
                }
            }
        }
    },
    created() {
        this.debouncedGetResults = debounce(this.getResults, 400);
    },
    mounted() {
        document.addEventListener("click", this.handleClickOutside);

        if (typeof this.location !== "undefined") {
            this.url = this.location.gl_url_new;
            this.keywords = this.location.gl_typed;
            this.placeId = this.location.gl_google_place_id;
            this.clicked = true;
            this.type = "location";
            this.aid = this.location.gl_aid;
            this.destination = [];
            this.destination.push({
                url: this.url,
                placeId: this.placeId,
                type: this.type,
                keywords: this.keywords,
                aid: this.aid
            });
            mutations.setDestination(this.destination);
        }

        if (!this.parsedUrl) {
            this.parsedUrl = {};
        } //If parsedUrl undefined
        this.parsedUrl.nights = parseInt(this.parsedUrl.nights);
        if (this.parsedUrl.nights > 0) {
            var arrive = this.parsedUrl.arrive.split("-");
            var nights = this.parsedUrl.nights;
            mutations.setNights(nights);
            this.date1 = format(arrive, "YYYY-MM-DD");
            this.date2 = addDays(this.date1, nights);
            this.date2 = format(this.date2, "YYYY-MM-DD");
        }
        if (this.parsedUrl.flexible > 0) {
            this.storeState.flexibleNights = parseInt(this.parsedUrl.flexible);
        }
    },
    beforeDestroy() {
        document.removeEventListener("click", this.handleClickOutside);
    },
    methods: {
        handleClickOutside(event) {
            const trigger = this.$refs.dateTrigger;
            const calendarComponent = this.$refs.calendarPopup;
            const calendar = calendarComponent ? calendarComponent.$el : null;

            if (
                this.isDPVisible &&
                trigger &&
                !trigger.contains(event.target) &&
                calendar &&
                !calendar.contains(event.target)
            ) {
                this.closeCalendar();
            }
        },
        highlight(result) {
            let text = result.gl_typed;
            if (this.unrestrictedSearch === true) {
                text = this.adminPrefix(result.type) + text;
            }

            if (result.type === "country") {
                text = text.bold(); // If country make <b>
            }

            if (this.storeState.pid === 0) {
                return text.replace(
                    new RegExp(this.keywords.trim(), "gi"),
                    '<span style="color: #0099ff;font-weight: bold;">$&</span>'
                ); // Highlight search text
            }
        },
        resetSearch() {
            this.keywords = "";
            this.clicked = false;
            this.url = "";
            this.placeId = "";
            this.geolock = {};
            this.type = "";
            this.destination = [];
            mutations.setDestination(this.dest);
        },
        async selectResult(result, selected) {
            this.clicked = true;
            this.aid = this.results[selected]["gl_aid"];
            this.keywords = this.results[selected]["gl_typed"];
            this.url = this.results[selected]["gl_url_new"];
            this.placeId = this.results[selected]["gl_google_place_id"];
            this.type = this.results[selected]["type"];
            if (
                result.type === "google" ||
                (result.type === "country" && result.gl_url_new === null)
            ) {
                try {
                    const response = await axios.post("/api/geo-lock/", result);
                    this.geolock = response.data;
                    this.aid = this.geolock["gl_aid"];
                    this.keywords = this.geolock["gl_typed"];
                    this.url = this.geolock["gl_url_new"];
                    this.placeId = this.geolock["gl_google_place_id"];
                    this.type = this.geolock["type"];
                    this.clicked = true;
                    this.results = [];
                } catch (error) {
                    console.error(error);
                }
            }
            this.results = [];
            this.destination.push({
                url: this.url,
                placeId: this.placeId,
                type: this.type,
                keywords: this.keywords,
                aid: this.aid
            });
            mutations.setDestination(this.destination);
        },
        getResults() {
            axios
                .get("/api/geo-complete/" + this.keywords.trim(), {
                    params: {
                        ...(this.storeState.unrestricted === true
                            ? {u: 1}
                            : {}) // If unrestricted is on, send query string to api
                    }
                })
                .then(response => (this.results = response.data))
                .catch(error => {
                    console.error(error);
                });
        },
        sendPID(pid) {
            mutations.setPID(pid);
        },
        forceRerender() {
            this.results.length = 0;
        },
        adminPrefix(type) {
            if (type === "location") {
                return "L> ";
            }
            if (type === "country") {
                return "C> ";
            }
            if (type === "google") {
                return "G> ";
            }
            if (type === "hidden") {
                return "H> ";
            }
            if (type === "redirect") {
                return "R> ";
            }
            if (type === "hidden-redirect") {
                return "HR> ";
            }
        },
        lazyDP() {
            if (this.isDPVisible === false) {
                new Promise(resolve => {
                    this.isDPVisible = true;
                    resolve();
                }).then(() => {
                    this.showPicker = true;
                });
            } else {
                this.isDPVisible = false;
                this.showPicker = false;
            }
        },
        onCancelMethod: function () {
            this.date1 = "";
            this.date2 = "";
            this.storeState.arrive = "";
            this.storeState.nights = 0;
        },
        formatDates: function (date1, date2) {
            var formattedDates = "";
            if (date2) {
                formattedDates =
                    format(date1, this.dateFormat) +
                    " - " +
                    format(date2, this.dateFormat);
                this.storeState.arrive = format(
                    date1,
                    this.arriveFormat
                ).toLowerCase();
                this.storeState.nights = differenceInCalendarDays(date2, date1);

                if (this.storeState.flexibleNights > 0) {
                    formattedDates +=
                        " (± " + this.storeState.flexibleNights + ")";
                }
            }
            return formattedDates;
        },
        formatArrive: function (date1) {
            var formattedArrive = "";
            if (date1) {
                formattedArrive = format(
                    date1,
                    this.arriveFormat
                ).toLowerCase();
                this.storeState.arrive = formattedArrive;
            } else {
                formattedArrive = 0;
            }
            return formattedArrive;
        },
        formatNights: function (date1, date2) {
            var formattedNights = "";
            if (date1) {
                formattedNights = differenceInCalendarDays(date2, date1);
                this.storeState.nights = formattedNights;
            } else {
                formattedNights = 0;
            }
            return formattedNights;
        },
        loadSearch() {
            this.isSearching = true;

            if (this.storeState.destination["0"]) {
                if (
                    this.storeState.destination["0"].type === "google" ||
                    this.storeState.destination["0"].url === null
                ) {
                    this.storeState.destination["0"].type = "google";
                    this.geoSetup(this.storeState.destination["0"]);
                } else {
                    window.location = this.searchUrl;
                }
            } else {
                window.location = this.searchUrl;
            }
        },
        async geoSetup(data) {
            try {
                const response = await axios.post("/api/geo-setup/", data);
                await mutations.updateUrl(response.data);
                window.location = this.searchUrl;
            } catch (ex) {
                console.error(ex);
            }
        },
        closeCalendar() {
            this.isDPVisible = false;
            this.showPicker = false;
        },
        setFlexibleDate(range) {
            this.storeState.flexibleNights = parseInt(range);
        }
    },
    computed: {
        isHomePage() {
            return window.location.pathname === "/";
        },

        unrestrictedSearch() {
            return store.state.unrestricted;
        },
        getToday: function () {
            let formattedToday = "";
            this.today = startOfToday();
            formattedToday = format(this.today, "YYYY-MM-DD");
            return formattedToday;
        },
        searchUrl() {
            if (this.storeState.destination.length > 0) {
                this.url =
                    window.location.origin +
                    "/" +
                    this.storeState.destination["0"].url +
                    "/";
                if (this.storeState.arrive > "" && this.storeState.nights > 0) {
                    this.url +=
                        "nights-" +
                        this.storeState.nights +
                        "/arrive-" +
                        this.storeState.arrive +
                        "/";
                    if (this.storeState.flexibleNights > 0) {
                        this.url +=
                            "flexible-" + this.storeState.flexibleNights + "/";
                    }
                }

                return this.url;
            }
            if (this.storeState.pid > 0) {
                this.url =
                    window.location.origin + "/p/" + this.storeState.pid + "/";

                if (this.storeState.arrive > "" && this.storeState.nights > 0) {
                    this.url +=
                        this.storeState.arrive +
                        "/" +
                        this.storeState.nights +
                        "/";
                }

                return this.url;
            }
        }
    }
};
</script>
<style scoped>
.search-calendar-popup {
    position: absolute;
    z-index: 99999999999999;
    background: white;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    border: 2px solid #dcdcdc;
    padding: 10px;
    margin-top: 1px;
}

@media (min-width: 768px) and (max-width: 1299px) {
    .homepage-style {
        transform: translateX(-34%);
    }
}

@media (min-width: 1023px) and (max-width: 1300px) {
    .destination-style {
        transform: translateX(-23%);
    }
}

@media (max-width: 767px) {
    .search-calendar-popup {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100vw;
        height: 100vh;
        margin: 0;
        border: none;
        border-radius: 0;
        z-index: 10000;
        box-shadow: none;
        overflow-y: auto;
        padding-left: 0;
        padding-right: 0;
    }
}
</style>
